import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { useProductReviewsAggregate } from "@react/hooks/useProductReviewsAggregate";
import { ProductHeroRatingView } from "@react/sections/ProductHero/ProductHeroRating/ProductHeroRating.view";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import { type ProductReviewsSettings } from "@root/tablescape/state";

type Props = {
  products?: { id: string; name: string }[]; // Product info that can be manually passed in (for React Product hero)
  sectionIdToLink?: string;
};

const ProductHeroRatingBase = ({ products, sectionIdToLink }: Props) => {
  const [productIds, setProductIds] = useState<string[]>([]);

  const sectionSettings = useSectionSettings<ProductReviewsSettings>(); // Product info and section ID also pulled from section settings (for Liquid Product Hero)
  const { rating } = sectionSettings.text;

  const resolvedProducts = useMemo(
    () => products ?? sectionSettings.products,
    [products, sectionSettings]
  );

  const resolvedSectionIdToLink = useMemo(
    () => sectionIdToLink ?? sectionSettings.sectionIdToLink,
    [sectionIdToLink, sectionSettings]
  );

  useEffect(() => {
    if (process.env.STOREFRONT_ENV === "staging") {
      import("@root/scripts/product-id-map.json").then((productIdMap) => {
        setProductIds(
          resolvedProducts.map((product) => {
            const resolvedProductId = String(
              product.id
            ) as keyof typeof productIdMap.staging.products;

            return String(
              productIdMap.staging.products[resolvedProductId]?.destinationId ??
                product.id
            );
          })
        );
      });
    }
    setProductIds(resolvedProducts.map((product) => String(product.id)));
  }, [resolvedProducts]);

  const {
    aggregateReviewCount,
    aggregateReviewScore,
    reviewsAggregateIsLoading,
  } = useProductReviewsAggregate(productIds);

  const aggregateReviewCountVerbatim = useMemo(() => {
    const scoreText = aggregateReviewScore ? `(${aggregateReviewScore}) ` : "";
    const resolvedCount = aggregateReviewCount ?? 0;
    const reviewWord =
      resolvedCount > 1
        ? rating.textReviews
        : resolvedCount === 1
        ? rating.textReview
        : "";
    const isAggregate =
      productIds.length > 1 &&
      sectionSettings.settings.showBundleReviewsAggregateDisclaimer;
    const countText = reviewWord
      ? `${aggregateReviewCount && aggregateReviewCount.toLocaleString("en")} ${
          isAggregate ? rating.textAggregated : ""
        } ${reviewWord}${isAggregate ? "*" : ""}`
      : "";
    return scoreText + countText;
  }, [
    aggregateReviewScore,
    aggregateReviewCount,
    productIds.length,
    sectionSettings.settings.showBundleReviewsAggregateDisclaimer,
    rating.textReviews,
    rating.textReview,
    rating.textAggregated,
  ]);

  const jumpToReviewsSection = useCallback(() => {
    const section = document.getElementById(resolvedSectionIdToLink);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  }, [resolvedSectionIdToLink]);

  return (
    <ProductHeroRatingView
      clickHandler={jumpToReviewsSection}
      isLoading={reviewsAggregateIsLoading || !aggregateReviewScore}
      rating={
        aggregateReviewScore ? parseFloat(aggregateReviewScore) : undefined
      }
      text={aggregateReviewCountVerbatim}
    />
  );
};

export const ProductHeroRating = memo(ProductHeroRatingBase);
